import { Money } from '@palaccount/money';

import type { MirrorLineInput, TotalsView } from './totals-mirror';

/**
 * SAL-01's discount-approval banners, computed exactly the way the server's
 * send gate computes its refusal (`effectiveDiscountPct` in
 * quotations.service.ts — BR-SAL-01-06): the EFFECTIVE total discount, lines
 * included (BR-SAL-01-08's proposed default), measured against the tenant's
 * `invoicing.max_discount_pct` (SAL-01 §16 question 2's proposed default —
 * the settings hub's number, never a hard-coded 10).
 *
 * The result is a BANNER, not a refusal: the spec's §14 rows put a sentence
 * in front of the discount while the document is still being written —
 * amber for a user who cannot approve it, green for one who can — and the
 * server refuses only the SEND. Both sentences are drawn by the editor from
 * i18n keys carrying the spec's exact wording.
 */

export interface DiscountGateView {
  /**
   * 'amber' = ERR-SAL-01-03 ("Discount of 15.0% exceeds the 10% threshold —
   * manager approval required before sending"); 'green' = ERR-SAL-01-04
   * ("Discount within your approval authority (15.0%)").
   */
  kind: 'amber' | 'green';
  /** One decimal, the shape the spec prints ("15.0"). */
  pct: string;
  /** Echoed for the amber sentence's second hole. */
  threshold: string;
}

const AMOUNT_RE = /^\d+(\.\d+)?$/;

/**
 * The effective total discount as a Money percentage, or null while the
 * figures cannot be trusted (half-typed rows, an invalid discount box, a
 * zero-priced document). Gross is the lines before ANY discount, each part
 * rounded first (N-7) so the arithmetic matches the stored subtotal's.
 */
export function effectiveDiscountPct(
  lines: readonly MirrorLineInput[],
  totals: Pick<TotalsView, 'subtotal' | 'discountAmount'>,
  currency: string,
): Money | null {
  let gross = Money.zero(currency);
  for (const line of lines) {
    const qty = line.qty.trim();
    const price = line.unitPrice.trim();
    if (!AMOUNT_RE.test(qty) || !AMOUNT_RE.test(price)) continue;
    gross = gross.plus(Money.of(price, currency).times(qty).rounded());
  }
  if (!gross.isPositive()) return null;
  const discountAmount = totals.discountAmount ?? '0.00';
  if (!AMOUNT_RE.test(totals.subtotal) || !AMOUNT_RE.test(discountAmount)) return null;
  const net = Money.of(totals.subtotal, currency).minus(Money.of(discountAmount, currency));
  return gross.minus(net).times('100').dividedBy(gross.toDecimalString());
}

/**
 * Which banner, if any. Over the threshold the permission decides the colour:
 * without approval authority the amber warning (ERR-SAL-01-03); with it, the
 * green confirmation instead — "A user who does hold that permission must
 * see the confirmation banner" (BR-SAL-01-07). At or under the threshold,
 * nothing.
 */
export function discountGate(
  lines: readonly MirrorLineInput[],
  totals: Pick<TotalsView, 'subtotal' | 'discountAmount'>,
  currency: string,
  threshold: string,
  holdsApprove: boolean,
): DiscountGateView | null {
  const pct = effectiveDiscountPct(lines, totals, currency);
  if (pct === null) return null;
  if (!pct.greaterThan(Money.of(threshold, currency))) return null;
  return { kind: holdsApprove ? 'green' : 'amber', pct: pct.toFixed(1), threshold };
}
